<template>
  <div class="dg" :style="frTokens">
    <!-- Consistent white app-chrome header (shared across custom layouts):
         Amazon logo + menu (unread bubble) + greeting + weather + a contextual
         sub-line (today's featured business + the passholder's state). -->
    <PassHeader :subline="headerSubline" @open-menu="drawerOpen = true">
      <!-- Native tracker: prizes remaining, compact, in the header top row. -->
      <!-- <template #tracker>
        <div class="dg-tracker" :aria-label="`${prizesLeft} of ${totalPrizePool} prizes remaining`">
          <Trophy :size="13" />
          <span class="dg-tracker__count">{{ prizesLeft }}<span class="dg-tracker__total">/{{ totalPrizePool }}</span></span>
        </div>
      </template> -->
    </PassHeader>

    <!-- Navigation drawer (opened by the PassHeader hamburger) -->
    <DgTopBar v-model:open="drawerOpen" />

    <!-- Pass hero — the pass title + How it works overlaid on the image, over a
         scrim so they stay legible on any partner image. -->
    <div class="dg-hero">
      <img v-if="headerImage" :src="headerImage" alt="" class="dg-hero__img" />
      <template v-else>
        <div class="dg-hero__fallback" />
        <div class="dg-hero__fallback-blur" />
      </template>
      <div class="dg-hero__scrim" />
      <button id="giveaway_how_btn" type="button" class="dg-hero__how gtm-click" aria-label="How it works" @click="showHow = true">
        <HelpCircle :size="13" /><span>How it works</span>
      </button>
    </div>

    <!-- ==================== Today's featured ==================== -->
    <section class="dg-featured-wrap">
      <div class="dg-featured">
        <div class="dg-featured__ribbon">
          <span class="dg-featured__ribbon-left"><Sparkles :size="12" />{{ featuredKicker }}</span>
          <span>Featured business</span>
        </div>

        <button type="button" class="dg-featured__hero" :aria-label="`View details for ${todayVenue.name}`" @click="openDetails(todayVenue)">
          <img :src="todayVenue.img" :alt="todayVenue.name" class="dg-featured__img" />
          <div class="dg-featured__scrim" />
          <span class="dg-featured__details-pill"><Info :size="12" />Details</span>
          <div class="dg-featured__overlay">
            <div class="dg-featured__kinds">
              <span v-for="k in todayVenue.kinds.slice(0, 3)" :key="k" class="dg-chip-glass">{{ k }}</span>
            </div>
            <h3 class="dg-featured__name">{{ todayVenue.name }}</h3>
            <div class="dg-featured__meta">
              <span class="dg-featured__hours"><Clock :size="12" />{{ todayVenue.hours }}</span>
            </div>
          </div>
        </button>

        <div class="dg-featured__body">
          <div class="dg-featured__prize">
            <div class="dg-featured__prize-label">Prize</div>
            <div class="dg-featured__prize-title">{{ prizeTitle }}</div>
            <div class="dg-featured__prize-sub">Winners chosen at random upon check-in.</div>
          </div>

          <!-- Pool exhausted: celebrate, never expose a guaranteed-loss entry -->
          <div v-if="poolExhausted && todayResult === 'unentered'" class="dg-stack">
            <div class="dg-lost"><Trophy :size="14" class="dg-ico-primary" /><span>All {{ ctx.totalPeriods }} prizes have been claimed — what a {{ ctx.entryUnit === 'day' ? 'month' : 'run' }}! Coupons below are still yours to use.</span></div>
            <button id="giveaway_directions_exhausted_btn" type="button" class="dg-btn dg-btn--muted gtm-click" @click="openDetails(todayVenue)">Visit {{ todayVenue.name }}</button>
          </div>

          <!-- Unentered -->
          <div v-else-if="todayResult === 'unentered'" class="dg-featured__cta-stack">
            <button type="button" class="dg-checkin-cta gtm-click" :class="{ 'dg-checkin-cta--disabled': !featuredIsCurrent }" id="giveaway_checkin_btn" :disabled="!featuredIsCurrent" @click="onStartCheckIn">
              <Sparkles :size="16" class="dg-checkin-cta__spark" />
              <span>{{ ctx.ctaEnter }}</span>
            </button>
            <a id="giveaway_directions_btn" :href="mapsUrl(todayVenue)" target="_blank" rel="noopener" class="dg-directions-link gtm-click">
              <Navigation :size="13" /><span>Directions</span>
            </a>
          </div>

          <!-- Won, not redeemed -->
          <div v-else-if="todayResult === 'won' && !redeemed" class="dg-stack">
            <template v-if="prizeRedeemOnMobile">
              <div class="dg-won-banner"><PartyPopper :size="16" /><span>You won today's prize!</span></div>
              <div :class="['dg-timer', { 'is-low': lowTime }]">
                <div class="dg-timer__row">
                  <div class="dg-timer__label"><Timer :size="14" /><span>Redeem within</span></div>
                  <span class="dg-timer__value">{{ formatCountdown(msLeft) }}</span>
                </div>
                <div class="dg-timer__track"><div class="dg-timer__fill" :style="{ width: pctLeft + '%' }" /></div>
                <div class="dg-timer__note">Unclaimed prizes are released back into the pool.</div>
              </div>
              <button type="button" class="dg-btn dg-btn--primary gtm-click" id="giveaway_redeem_btn" @click="onOpenReward">Claim Your Prize</button>
            </template>
            <template v-else>
              <button type="button" class="dg-btn dg-btn--won gtm-click" id="giveaway_see_prize_btn" @click="onOpenReward">See your prize</button>
              <button type="button" class="dg-btn dg-btn--muted dg-btn--icon gtm-click" id="giveaway_see_ticket_btn" @click="onSetStage('ticket')"><Ticket :size="14" /><span>View ticket</span></button>
            </template>
          </div>

          <!-- Won, redeemed — ticket access follows products.ticket_settings.hide_ticket -->
          <div v-else-if="todayResult === 'won' && redeemed" class="dg-stack">
            <div class="dg-redeemed"><Check :size="16" /><span>{{ redeemedBannerCopy }}</span></div>
            <template v-if="ticketStillVisible">
              <button id="giveaway_view_ticket_btn" type="button" class="dg-btn dg-btn--muted dg-btn--icon gtm-click" @click="onSetStage('ticket')">
                <Ticket :size="14" /><span>View ticket</span>
              </button>
              <div v-if="showTicketHideCountdown" class="dg-featured__oneentry">Ticket available for {{ formatCountdown(ticketHideMsLeft) }}</div>
            </template>
          </div>

          <!-- Expired -->
          <div v-else-if="todayResult === 'expired'" class="dg-stack">
            <div class="dg-expired"><RotateCcw :size="14" /><span>Your redemption window closed. The prize has been returned to today's pool for someone else to win.<template v-if="checkInRepeats"> You can try again!</template></span></div>
            <button v-if="checkInRepeats" id="giveaway_checkin_again_btn" type="button" class="dg-btn dg-btn--primary gtm-click" @click="onResetToday">Check in again</button>
            <button v-else type="button" class="dg-btn dg-btn--muted" disabled>{{ checkedInLabel }}</button>
          </div>

          <!-- Lost -->
          <div v-else-if="todayResult === 'lost'" class="dg-lost"><Check :size="14" /><span>Entered — not a winner. {{ ctx.tryAgainSoon }}</span></div>

          <div v-if="isEntered && todayResult !== 'expired'" class="dg-featured__oneentry">One entry per person per {{ ctx.entryUnit }}.</div>
        </div>
      </div>
    </section>

    <!-- ==================== Calendar strip ==================== -->
    <section class="dg-section">
      <div class="dg-section__head">
        <Trophy :size="16" class="dg-section__icon" />
        <div class="dg-section__titles">
          <h2 class="dg-section__title">{{ ctx.calendarTitle }}</h2>
          <div class="dg-section__meta">{{ ctx.calendarMeta }}</div>
        </div>
      </div>
      <div ref="calEl" class="dg-cal">
        <div class="dg-cal__track">
          <button
            v-for="v in participatingVenues"
            :key="v.id"
            type="button"
            :class="['dg-cal__chip', { 'is-today': v.featuredDay === ctx.currentPeriod, 'is-wide': ctx.cadence === 'monthly' }]"
            @click="v.featuredDay === ctx.currentPeriod ? focusFeatured() : openDetails(v)"
          >
            <span v-if="v.periodPrefix" class="dg-cal__prefix">{{ v.periodPrefix }}</span>
            <span v-else class="dg-cal__spacer" />
            <span class="dg-cal__num">{{ v.periodShort }}</span>
            <span class="dg-cal__status">
              <Sparkles v-if="v.featuredDay === ctx.currentPeriod" :size="12" class="dg-ico-primary" />
              <template v-else-if="v.featuredDay < ctx.currentPeriod">
                <Check v-if="pastOutcomes[v.featuredDay] === 'won'" :size="12" class="dg-ico-emerald" :stroke-width="3" />
                <RotateCcw v-else-if="pastOutcomes[v.featuredDay] === 'expired'" :size="12" class="dg-ico-muted" :stroke-width="2.5" />
                <span v-else class="dg-cal__dot" />
              </template>
              <Lock v-else :size="10" class="dg-ico-muted-soft" />
            </span>
          </button>
        </div>
      </div>
    </section>

    <!-- ==================== All participating businesses ==================== -->
    <section class="dg-section dg-section--list">
      <div class="dg-section__head dg-section__head--tight">
        <MapPin :size="16" class="dg-section__icon" />
        <div class="dg-section__titles">
          <h2 class="dg-section__title">{{ favoritesOnly ? 'Your favorites' : ctx.listTitle }}</h2>
          <div class="dg-section__meta">{{ favoritesOnly ? `${favorites.size} saved` : `One featured per ${ctx.entryUnit}` }}</div>
        </div>
      </div>
      <p v-if="!favoritesOnly" class="dg-lockcopy"><Lock :size="12" /><span>{{ ctx.listLockCopy }}</span></p>

      <!-- Location prompt -->
      <div v-if="locationStatus !== 'granted'" class="dg-locprompt">
        <Navigation :size="14" class="dg-ico-primary" />
        <div class="dg-locprompt__text">
          {{ locationStatus === 'denied'
            ? "Location off — we can't show distances. Enable in your browser settings to see what's closest."
            : 'Share your location to see how far each business is from you.' }}
        </div>
        <button v-if="locationStatus === 'unasked'" id="giveaway_location_enable_btn" type="button" class="dg-locprompt__btn gtm-click" @click="requestLocation">Enable</button>
      </div>

      <div class="dg-rows">
        <div
          v-for="v in displayedVenues"
          :key="v.id"
          :class="['dg-row', { 'is-today': v.featuredDay === ctx.currentPeriod }]"
        >
          <button type="button" class="dg-row__thumb-btn" @click="openDetails(v)">
            <img v-if="v.img" :src="v.img" alt="" class="dg-row__thumb" loading="lazy" />
          </button>
          <button type="button" class="dg-row__main" @click="openDetails(v)">
            <div class="dg-row__badges">
              <span class="dg-row__period">{{ v.periodLabel }}</span>
              <span v-if="v.featuredDay === ctx.currentPeriod" class="dg-row__today">{{ ctx.todayChip }}</span>
              <span v-if="v.featuredDay < ctx.currentPeriod && pastOutcomes[v.featuredDay] === 'won'" class="dg-row__winner">Winner</span>
              <span v-if="v.featuredDay === ctx.currentPeriod && todayResult === 'expired'" class="dg-row__forfeit"><RotateCcw :size="10" /> Forfeited</span>
              <span v-if="v.coupons?.length" class="dg-row__coupon"><Ticket :size="10" /> {{ v.coupons.length > 1 ? `${v.coupons.length} Coupons` : 'Coupon' }}</span>
              <span v-if="v.videoUrl || v.audioUrl" class="dg-row__media" :aria-label="v.videoUrl ? 'Has video' : 'Has audio tour'">
                <PlayCircle v-if="v.videoUrl" :size="11" />
                <Volume2 v-if="v.audioUrl" :size="11" />
              </span>
            </div>
            <div class="dg-row__name">{{ v.name }}</div>
            <div v-if="v.kinds.length" class="dg-row__meta">{{ v.kinds.join(' · ') }}</div>
            <div v-if="rowDistance(v)" class="dg-row__meta dg-row__distance">{{ rowDistance(v) }}</div>
          </button>
          <div class="dg-row__right">
            <button
              v-if="v.featuredDay === ctx.currentPeriod && !poolExhausted && todayResult === 'unentered'"
              id="giveaway_row_checkin_btn"
              type="button"
              class="dg-row__checkin gtm-click"
              @click="onStartCheckIn"
            >Check in</button>
            <span v-else class="dg-row__status">
              <component :is="rowStatus(v).icon" v-if="rowStatus(v).icon" :size="12" /> {{ rowStatus(v).label }}
            </span>
            <button type="button" class="dg-row__fav" :aria-label="favorites.has(v.id) ? 'Remove favorite' : 'Add favorite'" @click="toggleFavorite(v.id)">
              <Heart :size="14" :fill="favorites.has(v.id) ? 'var(--fr-primary)' : 'none'" :color="favorites.has(v.id) ? 'var(--fr-primary)' : 'currentColor'" :stroke-width="2" />
            </button>
          </div>
        </div>

        <!-- Favorites filter on, but nothing saved yet -->
        <div v-if="favoritesOnly && !displayedVenues.length" class="dg-fav-empty">
          <Heart :size="22" />
          <p>No favorites yet — tap the <Heart :size="12" :stroke-width="2.5" /> on any business to save it here.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Overlays ==================== -->
    <CheckInSheet :open="stage === 'checkin'" :spec="effectiveCheckIn" :theme="frTokens" :recovery-copy="recoveryCopy" :submit="entrySubmit" @close="onCloseStage" @success="onCheckInSuccess" />

    <GiftBoxReveal
      v-if="stage === 'reveal' && pendingOutcome"
      :outcome="pendingOutcome"
      :venue-name="todayVenue.name"
      :prize-title="prizeTitle"
      :prize-img="currentPrize?.img"
      :brand-color="colors.primary"
      :navy-color="colors.accent"
      :lose-message="loseMessage"
      @reveal-complete="onCommitReveal"
      @close="onDismissReveal"
    />

    <ProductDetailSheet
      :open="['product', 'product-confirm'].includes(stage)"
      :offer="productOffer"
      :theme="frTokens"
      :cta-label="prizeRedeemOnMobile ? 'Redeem Prize' : 'View Ticket'"
      :cta-variant="prizeRedeemOnMobile ? 'primary' : 'muted'"
      :bypass-availability="!prizeRedeemOnMobile"
      @close="onCloseStage"
      @continue="onPrizeContinue"
    />

    <CheckInSheet
      v-if="productCheckIn"
      :open="stage === 'product-validate'"
      :spec="productCheckIn"
      :theme="frTokens"
      :recovery-copy="recoveryCopy"
      :submit="prizeSubmit"
      @close="onCloseStage"
      @success="onProductValidated"
    />

    <!-- product-confirm (virtual products, no in-person validation) -->
    <Teleport to="body">
      <div v-if="stage === 'product-confirm'" class="dg-overlay" data-overlay :style="frTokens">
        <div class="dg-sheet" @click.stop>
          <div class="dg-sheet__body" data-scrollable>
            <h3 class="dg-sheet__title">Confirm redemption</h3>
            <p class="dg-sheet__text">Only tap confirm when you're ready to redeem in person.</p>
            <button id="giveaway_confirm_redeem_btn" type="button" class="dg-btn dg-btn--primary gtm-click" @click="onConfirmReveal">Confirm</button>
            <button type="button" class="dg-btn dg-btn--text" @click="onSetStage('product')">Cancel</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Ticket: the EXACT existing Mobile Pass ticket (modules/Tickets
         TicketsModal) so merchants see the ticket they already know. -->
    <TicketsModal
      :open="stage === 'ticket'"
      :product="ticketProduct"
      :product-type="ticketModalType"
      @close-modal="onCloseStage"
      @update:open="val => !val && onCloseStage()"
    />

    <Teleport to="body">
      <div v-if="errorMessage" class="dg-overlay dg-overlay--error" data-overlay :style="frTokens">
        <div class="dg-sheet" @click.stop>
          <div class="dg-sheet__body dg-sheet__body--center" data-scrollable>
            <h3 class="dg-sheet__title">Couldn't redeem</h3>
            <p class="dg-sheet__text">{{ errorMessage }}</p>
            <button id="giveaway_error_close_btn" type="button" class="dg-btn dg-btn--primary gtm-click" @click="errorMessage = ''">Close</button>
          </div>
        </div>
      </div>
    </Teleport>


    <!-- how it works -->
    <Teleport to="body">
      <div v-if="showHow" class="dg-overlay" data-overlay :style="frTokens">
        <div class="dg-sheet" @click.stop>
          <button type="button" class="dg-sheet__close" aria-label="Close" @click="showHow = false"><X :size="18" /></button>
          <div class="dg-sheet__body" data-scrollable>
            <ClampedText v-if="passDescription" class="dg-pass-desc" :max-chars="DESCRIPTION_PREVIEW_MAX_CHARS" :text="passDescription" />
            <div class="dg-sheet__head">
              <h3 class="dg-sheet__title">How it works</h3>
            </div>
            <ol class="dg-how">
              <li v-for="(step, i) in howSteps" :key="i"><span class="dg-how__num">{{ i + 1 }}</span><span>{{ step }}</span></li>
            </ol>
            <p class="dg-how__fine">Winners are selected randomly and spaced across the featured business's open hours. Once the daily pool is claimed, entries still help you discover great local businesses.</p>
          </div>
        </div>
      </div>
    </Teleport>

    <PlaceDetailSheet
      :open="!!detailsPlace"
      :place="detailsPayload"
      :theme="frTokens"
      :accent-color="colors.primary"
      :timezone="programTz"
      @close="closeDetails"
      @redeem-coupon="onRedeemCoupon"
    />

    <!-- Coupon redemption: place coupon → product-detail page → coupon ticket -->
    <ProductDetailSheet
      :open="!!couponOffer"
      :offer="couponOffer"
      :theme="frTokens"
      :cta-label="couponOffer?.ctaLabel || 'Redeem'"
      :hide-cta="!!couponOffer?.exhausted"
      @close="couponOffer = null"
      @continue="onCouponContinue"
    >
      <TicketsHistory
        v-if="couponDetailProduct"
        :product="couponDetailProduct"
        product-type="coupon"
      />
    </ProductDetailSheet>
    <TicketsModal
      :open="couponTicketOpen"
      :product="couponTicketProduct"
      product-type="coupon"
      @close-modal="couponTicketOpen = false"
      @hide-ticket="couponTicketOpen = false"
      @update:open="val => !val && (couponTicketOpen = false)"
    />
    <CheckInSheet
      v-if="couponCheckIn"
      :open="!!couponCheckIn"
      :spec="couponCheckIn"
      :theme="frTokens"
      :recovery-copy="recoveryCopy"
      :submit="couponSubmit"
      @close="onCouponCheckInClose"
      @success="onCouponValidated"
    />

    <FilterPill
      :theme="frTokens"
      placement="bottom-right"
      :favorites-active="favoritesOnly"
      :favorites-count="favorites.size"
      @open-map="onOpenBrowse(false, true)"
      @open-filter="onOpenBrowse(true)"
      @toggle-favorites="onToggleFavoritesFilter"
    />

    <BrowseOverlay
      :open="browseOpen"
      :open-filters="browseFiltersOpen"
      :open-map="browseMapOpen"
      :venues="browseVenues"
      :config="browseConfig"
      :timezone="programTz"
      :theme="frTokens"
      :title="ctx.listTitle"
      :kicker="ctx.calendarMeta"
      :favorites="favorites"
      @close="browseOpen = false"
      @select="onBrowseSelect"
      @toggle-favorite="toggleFavorite"
      @check-in="onBrowseCheckIn"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import {
  Sparkles, MapPin, Clock, Trophy, Check, Lock, X, Timer,
  HelpCircle, Ticket, Heart, Navigation, RotateCcw, Info, PartyPopper,
  Volume2, PlayCircle
} from '@lucide/vue'
import { useFrTokens } from './composables/useFrTokens'
import { useOverlayScrollLock } from '@/composables/useOverlayScrollLock'
import CheckInSheet from '@/components/shared/pass/validation/CheckInSheet.vue'
import PlaceDetailSheet from '@/components/shared/pass/PlaceDetailSheet.vue'
import ProductDetailSheet from '@/components/shared/pass/ProductDetailSheet.vue'
import FilterPill from '@/components/shared/pass/FilterPill.vue'
import BrowseOverlay from '@/components/shared/pass/BrowseOverlay.vue'
import { getOpenStatus, resolveWeekly, nowInZone } from '@/components/shared/pass/hours'
import { getVenueDistance, mapsUrl, getLocalDate } from '@/helpers'
import GiftBoxReveal from './components/GiftBoxReveal.vue'
import DgTopBar from './components/DgTopBar.vue'
import PassHeader from '@/components/shared/pass/PassHeader.vue'
import ClampedText from '@/components/shared/ClampedText/index.vue'
import { DESCRIPTION_PREVIEW_MAX_CHARS } from '@/constants/descriptionPreview'
import TicketsModal from '@/components/modules/Tickets/components/TicketsModal.vue'
import TicketsHistory from '@/components/modules/Tickets/components/TicketsHistory.vue'
import ticketHelpers from '@/components/modules/Tickets/helpers'

const store = useStore()
const frTokens = useFrTokens()
useOverlayScrollLock()

const gv = name => computed(() => store.getters[`giveaway/${name}`])
const ctx = gv('ctx')
const todayVenue = gv('todayVenue')
const participatingVenues = gv('participatingVenues')
const meterPct = gv('meterPct')
const prizesLeft = gv('prizesLeft')
const totalPrizePool = gv('totalPrizePool')
const poolExhausted = gv('poolExhausted')
const effectiveCheckIn = gv('effectiveCheckIn')
const checkedInAt = computed(() => store.state.giveaway.checkedInAt)
const checkInRepeats = computed(() => !!todayVenue.value?.checkInRepeat)
const checkedInLabel = computed(() =>
  checkedInAt.value ? `Checked in at ${getLocalDate(checkedInAt.value, 'MM/DD/YY [at] h:mm a')}` : 'Checked in'
)
const productCheckIn = gv('productCheckIn')
const productTerms = gv('productTerms')
const colors = gv('colors')
const pastOutcomes = gv('pastOutcomes')
const redemptionMinutes = gv('redemptionMinutes')
const recoveryCopy = gv('recoveryCopy')
const headerImage = gv('headerImage')
const programTz = gv('programTz')
const passDescription = gv('passDescription')
const ticketSettings = gv('ticketSettings')
const ticketHideMs = gv('ticketHideMs')
const currentPrize = gv('currentPrize')
const howItWorksProgram = gv('howItWorks')
const prizeProductId = gv('prizeProductId')

const howSteps = computed(() => howItWorksProgram.value?.steps || [])
const prizeTitle = computed(() => currentPrize.value?.title || '')
const featuredIsCurrent = computed(() => !!todayVenue.value && todayVenue.value.featuredDay === ctx.value.currentPeriod)
const featuredKicker = computed(() => {
  if (!todayVenue.value) return ''
  return featuredIsCurrent.value
    ? `${ctx.value.todayChip} · ${todayVenue.value.periodLabel}`
    : todayVenue.value.periodLabel
})

const st = k => computed(() => store.state.giveaway[k])
const cadence = st('cadence')
const stage = st('stage')
const todayResult = st('todayResult')
const pendingOutcome = st('pendingOutcome')
const wonAt = st('wonAt')
const redeemed = st('redeemed')
const redeemedAt = st('redeemedAt')
const prizeRedeemOnMobile = computed(() => !!currentPrize.value?.redeemOnDashboard)
const userLoc = computed(() => store.getters['userLocation/getUserLocation'])
const locationStatus = computed(() => store.getters['userLocation/getLocationStatus'])
function venueDistanceMi(v) {
  if (locationStatus.value !== 'granted') return undefined
  const d = getVenueDistance(v, userLoc.value)
  return d == null ? undefined : +d.toFixed(1)
}


// Local UI state
const showHow = ref(false)
const detailsPlace = ref(null)
const giveawayDealId = gv('giveawayDealId')
const favorites = computed(() => new Set(store.getters['favorites/getFavorites'](giveawayDealId.value)))
const favoritesOnly = ref(false)
// The "All N businesses" list, filtered to favorites when the heart pill is on.
const displayedVenues = computed(() =>
  favoritesOnly.value
    ? participatingVenues.value.filter(v => favorites.value.has(v.id))
    : participatingVenues.value
)
const browseOpen = ref(false)
const browseMapOpen = ref(false)
const browseFiltersOpen = ref(false)
const drawerOpen = ref(false)
const errorMessage = ref('')

// Countdown ticker
const now = ref(Date.now())
let timer = null

// Calendar strip: keep the current period (today/this hour/week/month) centered
// in the scroll container on load and whenever the cadence changes.
const calEl = ref(null)
function centerToday(behavior = 'auto') {
  const cal = calEl.value
  const chip = cal?.querySelector('.dg-cal__chip.is-today')
  if (!cal || !chip) return
  const delta =
    (chip.getBoundingClientRect().left - cal.getBoundingClientRect().left) -
    (cal.clientWidth - chip.offsetWidth) / 2
  cal.scrollBy({ left: delta, behavior })
}

onMounted(() => {
  timer = setInterval(() => { now.value = Date.now() }, 1000)
  nextTick(() => centerToday())
  requestLocation()
})
onBeforeUnmount(() => clearInterval(timer))

// Re-center when the cadence swaps the calendar out for a new current period.
watch(cadence, () => nextTick(() => centerToday()))

// Live validation handlers (null in mock mode → the sheet bodies keep their
// demo behavior). Every verdict is the server's: the entry sheet posts to the
// check-in endpoint (which also runs the draw), the prize sheet to the redeem
// endpoint; params.validate marks a combo's GPS pre-step.
const entrySubmit = (params = {}) =>
  params.validate
    ? store.dispatch('giveaway/validateEntry', params)
    : store.dispatch('giveaway/submitEntry', params)
const prizeSubmit = (params = {}) => store.dispatch('giveaway/submitPrizeRedemption', params)

const serverExpiresAt = gv('expiresAt')
const expiresAt = computed(() => prizeRedeemOnMobile.value
  ? (serverExpiresAt.value || (wonAt.value ? wonAt.value + redemptionMinutes.value * 60000 : null))
  : null)
const msLeft = computed(() => {
  if (!expiresAt.value) return 0
  const total = redemptionMinutes.value * 60000
  return Math.min(total, Math.max(0, expiresAt.value - now.value))
})
const pctLeft = computed(() => {
  const total = redemptionMinutes.value * 60000
  return Math.max(0, Math.min(100, (msLeft.value / total) * 100))
})
const lowTime = computed(() => msLeft.value > 0 && msLeft.value < 5 * 60000)
const isEntered = computed(() => todayResult.value !== 'unentered')

/* ---- Post-redemption ticket visibility (products.ticket_settings.hide_ticket) ----
 * Same semantics the module system already applies (store/orderProducts.js +
 * Tickets/helpers.js): type null = ticket stays accessible forever; 'immediate' =
 * 10-min grace; minutes/hours/days = hide after that duration from first redemption. */
const ticketHideCfg = computed(() => ticketSettings.value.hide_ticket.after_redemption)
const ticketHideMsLeft = computed(() => {
  if (!redeemed.value) return 0
  if (ticketHideMs.value === undefined) return Infinity // never hide
  if (!redeemedAt.value) return 0
  return Math.max(0, redeemedAt.value + ticketHideMs.value - now.value)
})
const ticketStillVisible = computed(() => redeemed.value && ticketHideMsLeft.value > 0)
const showTicketHideCountdown = computed(
  () => ticketStillVisible.value && !!ticketHideCfg.value.timer && Number.isFinite(ticketHideMsLeft.value)
)
// When the ticket hides, the program's configured message wins; default keeps today's copy.
const redeemedBannerCopy = computed(() =>
  ticketStillVisible.value
    ? 'Prize redeemed. Enjoy!'
    : (ticketHideCfg.value.message || 'Prize redeemed. Enjoy!')
)

const dispatch = (a, p) => store.dispatch(`giveaway/${a}`, p)

// Auto-expire the win when the countdown hits zero (returns prize to the pool).
watch(
  () => todayResult.value === 'won' && !redeemed.value && expiresAt.value && msLeft.value <= 0 && prizeRedeemOnMobile.value,
  val => { if (val) dispatch('expireWin') }
)

// Contextual header sub-line — blends the passholder's state with today's
// featured business/prize (all from signals already in the pass). Cadence-aware.
const headerSubline = computed(() => {
  const venue = todayVenue.value.name
  const period = { day: "Today's", hour: "This hour's", week: "This week's", month: "This month's" }[ctx.value.entryUnit] || "Today's"
  if (poolExhausted.value && todayResult.value === 'unentered') {
    return `${period} prizes are claimed — still worth a stop at ${venue}.`
  }
  switch (todayResult.value) {
    case 'won':
      if (!prizeRedeemOnMobile.value) return 'You won a prize — enjoy!'
      return redeemed.value
        ? 'Prize redeemed — enjoy!'
        : 'You won a prize — redeem it before the timer runs out.'
    case 'lost':
      return prizesLeft.value > 0
        ? `You're in for the draw — ${prizesLeft.value} prizes still up for grabs.`
        : "You're in for the draw. Good luck!"
    case 'expired':
      return 'Your redemption window closed — you can check in again.'
    default: // unentered
      return `${period} spotlight is ${venue} — check in to enter.`
  }
})

const loseMessage = computed(() => {
  const count = prizesLeft.value
  const stillUp = count > 0
    ? `, but there are still ${count} ${count === 1 ? 'prize' : 'prizes'} up for grabs ${ctx.value.meterWindow}`
    : ''
  return `Thanks for checking in! You didn't win this time${stillUp}! Don't forget to enjoy ${todayVenue.value.name} while you're here, and good luck on your next check-in!`
})

const productOffer = computed(() => ({
  brand: todayVenue.value.name,
  headline: currentPrize.value?.title,
  img: currentPrize.value?.img || todayVenue.value?.img || null,
  sub: undefined,
  expires: !prizeRedeemOnMobile.value ? undefined : (msLeft.value > 0 ? `Redeem within ${formatCountdown(msLeft.value)}` : 'Expired'),
  // Blackout dates are meaningless (and read as gotcha fine-print) on a prize
  // that expires in minutes — only surface them on date-based expiries.
  terms: msLeft.value > 0 ? { ...productTerms.value, blackoutDates: [] } : productTerms.value
}))

// The existing app TicketsModal heading variant per giveaway product type.
const ticketModalType = computed(() => {
  const map = { 'admission-ticket': 'admission', coupon: 'coupon', 'gift-card': 'coupon', 'physical-pickup': 'admission' }
  return map[currentPrize.value?.type]
})

// The REAL prize order product from the backend payload, in the shape
// modules/Tickets expects (so merchants see the ticket they already know).
const ticketProduct = computed(() => {
  void stage.value
  return prizeProductId.value ? store.getters['products/find'](prizeProductId.value) : null
})

// ---- Browse overlay: map venues into the BrowseVenue shape + build config ----
const browseVenues = computed(() =>
  participatingVenues.value.map(v => {
    const isFeaturedNow = v.featuredDay === ctx.value.currentPeriod
    const couponCount = v.coupons?.length ?? 0
    return {
      id: v.id,
      name: v.name,
      subtitle: v.kinds.join(' · '),
      img: v.img,
      hours: v.hours,
      hoursWeekly: v.hoursWeekly,
      address: v.address,
      lat: v.latitude,
      lng: v.longitude,
      // Distance only reads when the user shared location (mirrors the list rows).
      distanceMi: venueDistanceMi(v),
      openNow: getOpenStatus(resolveWeekly(v.hoursWeekly, v.hours), nowInZone(programTz.value)).isOpen,
      facets: { category: v.kinds },
      labels: v.kinds,
      featured: isFeaturedNow,
      featuredLabel: isFeaturedNow ? ctx.value.todayChip : v.periodLabel,
      featuredColor: colors.value.primary,
      badge: couponCount ? (couponCount > 1 ? `${couponCount} coupons` : 'Coupon') : undefined,
      audio: !!v.audioUrl,
      video: !!v.videoUrl,
      canCheckIn: isFeaturedNow && !poolExhausted.value && todayResult.value === 'unentered'
    }
  })
)
const browseConfig = computed(() => {
  const kinds = [...new Set(participatingVenues.value.flatMap(v => v.kinds))].sort()
  return {
    searchPlaceholder: 'Search businesses or categories…',
    facets: [{ id: 'category', label: 'Category', options: kinds.map(k => ({ value: k, label: k })) }]
  }
})
function onBrowseSelect(v) {
  const raw = participatingVenues.value.find(x => x.id === v.id)
  if (raw) openDetails(raw)
}
function onBrowseCheckIn(v) {
  browseOpen.value = false
  dispatch('startCheckIn')
}

// "on/at/in" reads correctly across cadences (Featured on Sep 5 / at 9 AM /
// in Week 3 / in September).
const FEATURED_PREP = { hour: 'at', day: 'on', week: 'in', month: 'in' }

const detailsPayload = computed(() => {
  const p = detailsPlace.value
  if (!p) return null
  const prep = FEATURED_PREP[ctx.value.entryUnit] || 'on'
  return {
    id: p.id,
    name: p.name,
    img: p.img,
    kinds: p.kinds,
    // Featured ribbon (top banner) — left-aligned "Featured on <period>",
    // matching the featured-business card's top banner style.
    featured: {
      kicker: `Featured ${prep} ${p.periodLabel}`
    },
    description: p.description,
    audioUrl: p.audioUrl,
    audioLabel: p.audioLabel,
    videoUrl: p.videoUrl,
    videoPoster: p.videoPoster,
    hoursString: p.hours,
    hoursWeekly: p.hoursWeekly,
    weeklyHours: (p.weekdayText || []).map(d => {
      const i = d.indexOf(': ')
      return i === -1 ? { day: d, hours: '' } : { day: d.slice(0, i), hours: d.slice(i + 2) }
    }),
    phone: p.phone,
    website: p.website,
    address: p.address,
    coupons: (p.coupons || []).map(c => ({ productId: c.productId, headline: c.headline, sub: c.sub, expires: c.expires, state: c.state }))
  }
})

// Helpers
function formatCountdown(ms) {
  const s = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r.toString().padStart(2, '0')}`
}
function rowDistance(v) {
  const dMi = venueDistanceMi(v)
  return dMi != null ? `${dMi} mi` : null
}
function rowStatus(v) {
  if (v.featuredDay < ctx.value.currentPeriod) {
    const o = pastOutcomes.value[v.featuredDay]
    if (o === 'won') return { label: 'Claimed', icon: null }
    if (o === 'no-winner') return { label: 'No winner', icon: null }
    if (o === 'expired') return { label: 'Forfeited', icon: RotateCcw }
    return { label: 'No entry', icon: null }
  }
  if (v.featuredDay === ctx.value.currentPeriod) {
    if (todayResult.value === 'won') return { label: 'Winner', icon: null }
    if (todayResult.value === 'expired') return { label: 'Forfeited', icon: RotateCcw }
    if (isEntered.value) return { label: 'Entered', icon: null }
    return { label: 'Claimed', icon: null }
  }
  return { label: 'Locked', icon: Lock }
}
function toggleFavorite(id) {
  store.dispatch('favorites/toggleFavorite', { dealId: giveawayDealId.value, venueId: id })
}
function openDetails(v) { detailsPlace.value = v }
function closeDetails() { detailsPlace.value = null }

// Redeeming a place coupon opens the shared product-detail page ("Before you
// redeem") built from the coupon, then its Redeem CTA reveals the coupon ticket.
// Kept as local state so it never touches the prize stage machine. The ticket
// modal stays mounted with an always-valid product (mirrors the prize flow) and
// is toggled by a boolean — TicketsModal reads order_products unconditionally.
const EMPTY_TICKET = { name: '', settings: {}, ticket_settings: {}, redemption_settings: { mode: 'InStore' }, order_products: [] }
const couponOffer = ref(null)
const couponDetailProduct = ref(null)
const couponTicketOpen = ref(false)
const couponTicketProduct = ref(EMPTY_TICKET)
const couponCheckIn = ref(null)
const couponPending = ref(null)
const COUPON_VALIDATION_METHODS = { gps: 'gps', pin: 'pin', gpsOrPin: 'gps_or_pin', qr: 'qr', quiz: 'quiz' }
function buildCouponSpec(product, venueName, radiusLabel) {
  const method = COUPON_VALIDATION_METHODS[product?.redemption_validation]
  if (!method) return null
  const spec = { method, venueName: venueName || 'the business' }
  if (method.includes('gps')) {
    spec.realGps = true
    spec.radiusLabel = radiusLabel || `at ${spec.venueName}`
  }
  if (method.includes('pin')) spec.pinLength = 4
  if (method === 'quiz') {
    const quiz = product?.settings?.quiz
    if (quiz) {
      spec.question = quiz.question
      spec.choices = quiz.choices ?? quiz.answers
      spec.correctIndex = quiz.correct_index ?? quiz.correctIndex
    }
  }
  return spec
}
function formatBlackout(bd) {
  const start = getLocalDate(bd.start_date, 'MMM D')
  if (!bd.end_date) return `${start}+`
  const end = getLocalDate(bd.end_date, 'MMM D')
  return start === end ? start : `${start} – ${end}`
}
async function onRedeemCoupon(coupon) {
  const dealId = store.state.giveaway.dealId
  try {
    await store.dispatch('assignments/getAssignments', {
      type: 'product',
      assignmentId: coupon.productId,
      dealId,
      offerId: store.state.offer?.id
    })
  } catch (e) {}
  const product = store.getters['products/find'](coupon.productId, dealId)
  if (ticketHelpers.activeTickets(product).length) {
    couponTicketProduct.value = product
    couponTicketOpen.value = true
    closeDetails()
    return
  }
  const venueName = detailsPlace.value?.name || 'the featured business'
  const assignment = store.getters['assignments/find'](coupon.productId)
  const fields = assignment?.modules?.find(m => m.type === 'DashboardFields')?.data?.fields || []
  const fieldValue = label => fields.find(f => f.label === label)?.value || null
  const deal = store.getters['deals/find'](dealId)
  const blackouts = ticketHelpers.getBlackoutDates(product, deal)
  couponDetailProduct.value = product
  couponOffer.value = {
    productId: coupon.productId,
    brand: venueName,
    headline: coupon.headline,
    sub: coupon.sub,
    expires: coupon.expires ? `Ends ${coupon.expires}` : undefined,
    ctaLabel: product?.language_tags?.REDEEM || 'Redeem',
    radiusLabel: detailsPlace.value?.checkIn?.radiusLabel,
    exhausted: coupon.state !== 'redeem',
    terms: {
      description: fieldValue('Description'),
      howToUse: fieldValue('How To Use'),
      restrictions: fieldValue('Restrictions'),
      blackoutDates: blackouts.map(formatBlackout),
      isBlackoutToday: blackouts.some(ticketHelpers.isInBlackoutRange)
    }
  }
  closeDetails()
}
function openCouponTicket(productId, dealId) {
  couponTicketProduct.value = store.getters['products/find'](productId, dealId) || EMPTY_TICKET
  couponTicketOpen.value = true
}
async function onCouponContinue() {
  const offer = couponOffer.value
  if (!offer) return
  couponOffer.value = null
  const dealId = store.state.giveaway.dealId
  const product = store.getters['products/find'](offer.productId, dealId)
  const orderProduct = (product?.order_products || []).find(op => !op.redeemed)
  const spec = buildCouponSpec(product, offer.brand, offer.radiusLabel)
  if (spec && orderProduct) {
    couponPending.value = { productId: offer.productId, orderProductId: orderProduct.id, redemptions: product?.redemptions }
    couponCheckIn.value = spec
    return
  }
  if (orderProduct) {
    const res = await dispatch('redeemCoupon', { orderProductId: orderProduct.id, redemptions: product?.redemptions })
    if (!res?.ok) {
      errorMessage.value = res?.message || 'Redemption failed.'
      return
    }
  }
  openCouponTicket(offer.productId, dealId)
}
const couponSubmit = (params = {}) => {
  const pending = couponPending.value
  if (!pending) return Promise.resolve({ ok: false })
  return dispatch('redeemCoupon', { orderProductId: pending.orderProductId, redemptions: pending.redemptions, ...params })
}
function onCouponValidated() {
  const pending = couponPending.value
  couponCheckIn.value = null
  couponPending.value = null
  if (pending) openCouponTicket(pending.productId, store.state.giveaway.dealId)
}
function onCouponCheckInClose() {
  couponCheckIn.value = null
  couponPending.value = null
}
function requestLocation() {
  store.dispatch('userLocation/setUserLocation')
}
// The filter pill opens the full browse overlay (search + category / distance /
// open-now filters + favorites over a faithful list of all participating places).
function onOpenBrowse(withFilters = false, asMap = false) {
  browseFiltersOpen.value = withFilters
  browseMapOpen.value = asMap
  browseOpen.value = true
}
// Heart pill: filter the on-page list to favorites in place; scroll to it on.
function onToggleFavoritesFilter() {
  favoritesOnly.value = !favoritesOnly.value
  if (favoritesOnly.value) {
    nextTick(() => document.querySelector('.dg-section--list')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }
}

// Today's calendar chip: pulse the featured card (source focusVenue behavior).
function focusFeatured() {
  const el = document.querySelector('.dg-featured')
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  const rgb = getComputedStyle(el).getPropertyValue('--fr-primary-rgb').trim() || '255, 153, 0'
  el.animate(
    [{ boxShadow: `0 0 0 0 rgba(${rgb}, 0.55)` }, { boxShadow: '0 0 0 14px rgba(0, 0, 0, 0)' }],
    { duration: 900, easing: 'ease-out' }
  )
}

// Tap delight: a gold/orange sparkle burst from the CTA + a haptic tick, then
// open the check-in sheet. Sparkles (not confetti) = "let's go!" energy without
// a false-win signal — the real confetti stays reserved for an actual win.
function burstSparkles(x, y) {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  const colors = ['#FFD814', '#FF9900', '#FFB84D', '#FFFFFF']
  const layer = document.createElement('div')
  layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:3000;'
  document.body.appendChild(layer)
  for (let i = 0; i < 26; i++) {
    const s = 5 + Math.random() * 6
    const p = document.createElement('div')
    p.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${s}px;height:${s}px;` +
      `background:${colors[i % colors.length]};border-radius:${Math.random() < 0.5 ? '50%' : '1px'};` +
      'will-change:transform,opacity;'
    layer.appendChild(p)
    const ang = Math.random() * Math.PI * 2
    const dist = 55 + Math.random() * 120
    const dx = Math.cos(ang) * dist
    const dy = Math.sin(ang) * dist - (50 + Math.random() * 70) // bias upward
    const rot = Math.random() * 640 - 320
    p.animate([
      { transform: 'translate(0,0) scale(0.2) rotate(0deg)', opacity: 0 },
      { transform: `translate(${dx * 0.55}px, ${dy * 0.55}px) scale(1) rotate(${rot * 0.5}deg)`, opacity: 1, offset: 0.4 },
      { transform: `translate(${dx}px, ${dy + 150}px) scale(0.3) rotate(${rot}deg)`, opacity: 0 }
    ], { duration: 760 + Math.random() * 340, easing: 'cubic-bezier(0.2,0.7,0.3,1)', fill: 'forwards' })
  }
  setTimeout(() => layer.remove(), 1200)
}
function onStartCheckIn() {
  const btn = document.getElementById('giveaway_checkin_btn')
  if (btn) {
    const r = btn.getBoundingClientRect()
    burstSparkles(r.left + r.width / 2, r.top + r.height / 2)
  } else {
    burstSparkles(window.innerWidth / 2, window.innerHeight * 0.7)
  }
  navigator.vibrate?.(20)
  dispatch('startCheckIn')
}
// The sheet body already submitted (live) or locally passed (mock); this
// only opens the reveal — checkInSuccess is source-aware.
const onCheckInSuccess = () => dispatch('checkInSuccess')
const onCommitReveal = () => dispatch('commitReveal')
const onDismissReveal = () => dispatch('dismissReveal')
const onOpenReward = () => dispatch('openReward')
const onRedeemFromTerms = () => dispatch('redeemFromTerms')
const onPrizeContinue = () => prizeRedeemOnMobile.value ? dispatch('redeemFromTerms') : dispatch('setStage', 'ticket')
const onProductValidated = () => dispatch('setStage', 'ticket')
// Confirm-only reveal (no in-person validation): settles server-side too.
const onConfirmReveal = async () => {
  const res = await dispatch('confirmRevealPrize')
  if (!res?.ok) errorMessage.value = res?.message || 'Redemption failed.'
}
const onCloseStage = () => dispatch('closeStage')
const onSetStage = s => dispatch('setStage', s)
const onResetToday = () => dispatch('resetToday')
</script>

<style lang="scss" scoped>
/* Faithful port of amazon/DailyGiveawayPass.tsx — Tailwind translated to scoped
   SCSS pixel-for-pixel. Amazon brand hex flows via --fr-* (useFrTokens). This is
   the sanctioned pixel-perfect exception to the Vuetify-first rule. */
.dg {
  position: relative;
  min-height: 100%;
  overflow-y: auto;
  background: var(--fr-bg);
  color: var(--fr-ink);
  font-family: var(--fr-font-display);
  --dg-shadow: 0 10px 30px rgba(35, 47, 62, 0.12);
}
.dg * { box-sizing: border-box; }

/* ---------- Pass hero (image + overlaid title & How it works) ---------- */
.dg-hero { position: relative; width: 100%; overflow: hidden; background: var(--fr-accent); }
.dg-hero__img { display: block; width: 100%; aspect-ratio: 2 / 1; object-fit: cover; }
/* Brand-color fallback when the program has no hero image (mirrors the app's
   ImageShowcase brand-color-fallback: tinted surface + soft blurred blob). */
.dg-hero__fallback {
  display: block; width: 100%; aspect-ratio: 2 / 1;
  background: linear-gradient(to bottom right, rgba(255, 255, 255, 0.18), rgba(0, 0, 0, 0.28));
}
.dg-hero__fallback-blur {
  position: absolute; width: 16rem; height: 16rem; right: -2rem; bottom: -2rem;
  background: rgba(0, 0, 0, 0.12); border-radius: 50%; filter: blur(48px);
  pointer-events: none;
}
/* Scrim keeps overlaid text legible over any partner image (dark bottom for the
   title, mild top for the How pill). */
.dg-hero__scrim {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.12) 45%, rgba(0, 0, 0, 0.28) 100%);
}
.dg-hero__how {
  position: absolute; top: 0.75rem; right: 0.75rem;
  display: inline-flex; align-items: center; gap: 0.3rem;
  border: 0; cursor: pointer; border-radius: 999px;
  background: rgba(0, 0, 0, 0.45); color: #fff;
  padding: 0.35rem 0.65rem;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: background 0.15s;
  &:hover { background: rgba(0, 0, 0, 0.62); }
}

/* Compact native tracker chip (prizes remaining) in the header top row. */
/* .dg-tracker {
  display: inline-flex; align-items: center; gap: 0.3rem;
  border-radius: 999px;
  background: rgba(var(--fr-primary-rgb), 0.12);
  color: var(--fr-primary);
  padding: 0.28rem 0.55rem;
  font-family: var(--fr-font-display, inherit);
}
.dg-tracker__count { font-size: 12px; font-weight: 800; font-variant-numeric: tabular-nums; }
.dg-tracker__total { color: var(--fr-muted); font-weight: 700; } */

/* ---------- Featured ---------- */
.dg-featured-wrap { margin-top: 1.25rem; padding: 0 1.25rem; }
.dg-featured { position: relative; overflow: hidden; border-radius: 1rem; background: var(--fr-surface); box-shadow: var(--dg-shadow), inset 0 0 0 1px rgba(28, 28, 30, 0.1); }
.dg-featured__ribbon {
  display: flex; align-items: center; justify-content: space-between; padding: 0.375rem 0.75rem;
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.24em; color: #fff;
  background: linear-gradient(90deg, var(--fr-primary) 0%, var(--fr-primary-light) 50%, var(--fr-primary) 100%);
}
.dg-featured__ribbon-left { display: flex; align-items: center; gap: 0.375rem; }
.dg-featured__hero { position: relative; display: block; aspect-ratio: 2 / 1; width: 100%; overflow: hidden; text-align: left; border: 0; padding: 0; cursor: pointer; background: var(--fr-accent); /* skeleton while the image loads */ }
.dg-featured__img { height: 100%; width: 100%; object-fit: cover; }
.dg-featured__scrim { position: absolute; inset: 0; background: linear-gradient(to top, rgba(35, 47, 62, 0.9), rgba(35, 47, 62, 0.3) 50%, transparent); }
.dg-featured__details-pill {
  position: absolute; right: 0.5rem; top: 0.5rem; display: inline-flex; align-items: center; gap: 0.25rem;
  border-radius: 999px; background: rgba(255, 255, 255, 0.85); padding: 0.25rem 0.5rem;
  font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); backdrop-filter: blur(4px);
}
.dg-featured__overlay { position: absolute; inset-inline: 0; bottom: 0; padding: 0.75rem; color: #fff; }
.dg-featured__kinds { display: flex; flex-wrap: wrap; gap: 0.25rem; }
.dg-chip-glass { border-radius: 999px; background: rgba(255, 255, 255, 0.2); padding: 0.125rem 0.375rem; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; backdrop-filter: blur(4px); }
.dg-featured__name { margin: 0.25rem 0 0; font-family: var(--fr-font-serif-display); font-size: 1.25rem; font-weight: 900; font-style: italic; line-height: 1.15; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3); }
.dg-featured__meta { margin-top: 0.25rem; display: flex; align-items: center; gap: 0.5rem; font-size: 11px; color: rgba(255, 255, 255, 0.8); }
.dg-featured__hours { display: flex; align-items: center; gap: 0.25rem; }

.dg-featured__body { padding: 0.75rem; }
.dg-featured__prize { margin-bottom: 0.5rem; border-radius: 0.5rem; background: var(--fr-bg); padding: 0.625rem; }
.dg-featured__prize-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-primary); }
.dg-featured__prize-title { font-family: var(--fr-font-serif-display); font-size: 0.875rem; font-weight: 900; line-height: 1.15; }
.dg-featured__prize-sub { font-size: 10px; color: var(--fr-muted); }
.dg-featured__cta-stack { display: flex; flex-direction: column; gap: 0.375rem; }
.dg-directions-link {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.3rem;
  width: 100%; border: 0; background: transparent; cursor: pointer;
  padding: 0.35rem; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--fr-muted); transition: color 0.15s;
  &:hover { color: var(--fr-ink); }
}
.dg-featured__oneentry { margin-top: 0.5rem; text-align: center; font-size: 10px; color: var(--fr-muted); }

/* ---------- Buttons ---------- */
.dg-btn { border: 0; cursor: pointer; border-radius: 0.75rem; padding: 0.625rem 0.75rem; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; transition: transform 0.1s, background 0.15s; width: 100%; }
.dg-btn:active { transform: scale(0.98); }
.dg-btn--primary { background: var(--fr-primary); color: var(--fr-accent); }
.dg-btn--won { background: linear-gradient(90deg, var(--fr-primary), var(--fr-primary-deep)); color: #fff; }
.dg-btn--muted { background: var(--fr-bg); color: var(--fr-ink); &:hover { background: rgba(28, 28, 30, 0.1); } }
.dg-btn:disabled { opacity: 0.55; cursor: default; pointer-events: none; }
.dg-btn--text { background: transparent; color: var(--fr-muted); }
/* Buttons that pair a lucide icon with their label: center the pair on one line. */
.dg-btn--icon { display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem; }

/* The hero "Tap in to enter" CTA — full-width, vibrant gradient, sparkle icon,
   static glow + one idle effect (shine sweep). The big delight moment is the
   sparkle burst fired on tap (burstSparkles) + haptic, not idle motion. */
.dg-checkin-cta {
  position: relative;
  overflow: hidden;
  border: 0;
  cursor: pointer;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border-radius: 0.9rem;
  padding: 0.95rem 0.75rem;
  font-family: var(--fr-font-display, inherit);
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fr-accent);
  background: linear-gradient(135deg, var(--fr-primary-light) 0%, var(--fr-primary) 45%, var(--fr-primary-deep) 100%);
  box-shadow: 0 8px 22px rgba(var(--fr-primary-rgb), 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transition: transform 0.1s, box-shadow 0.15s;

  &:active { transform: scale(0.97); }

  // Shine sweep — the single idle effect: a light streak periodically crossing.
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 45%;
    height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.55), transparent);
    transform: skewX(-20deg);
    animation: dg-cta-shine 3s ease-in-out infinite;
  }
}
.dg-checkin-cta--disabled {
  cursor: default;
  color: rgba(28, 28, 30, 0.45);
  background: rgba(28, 28, 30, 0.08);
  box-shadow: inset 0 0 0 1px rgba(28, 28, 30, 0.08);
  &:active { transform: none; }
  &::after { display: none; }
}
.dg-checkin-cta__spark { flex-shrink: 0; }
@keyframes dg-cta-shine {
  0% { left: -60%; }
  55%, 100% { left: 130%; }
}
// Respect reduced-motion — keep the vibrant gradient/glow, drop the shine sweep.
@media (prefers-reduced-motion: reduce) {
  .dg-checkin-cta::after { display: none; }
}

/* ---------- Won / states ---------- */
.dg-stack { display: flex; flex-direction: column; gap: 0.5rem; }
.dg-won-banner { display: flex; align-items: center; gap: 0.5rem; border-radius: 0.75rem; padding: 0.625rem; font-size: 12px; font-weight: 600; color: #fff; background: linear-gradient(90deg, var(--fr-primary), var(--fr-primary-deep)); span { flex: 1; } }
.dg-timer { border-radius: 0.75rem; padding: 0.625rem; background: var(--fr-bg); box-shadow: inset 0 0 0 1px rgba(28, 28, 30, 0.1); &.is-low { background: #fef2f2; box-shadow: inset 0 0 0 1px #fecaca; } }
.dg-timer__row { display: flex; align-items: center; justify-content: space-between; }
.dg-timer__label { display: flex; align-items: center; gap: 0.375rem; color: var(--fr-primary); span { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-muted); } }
.dg-timer.is-low .dg-timer__label { color: #dc2626; }
.dg-timer__value { font-family: var(--fr-font-serif-display); font-size: 1.125rem; font-weight: 900; font-variant-numeric: tabular-nums; line-height: 1; color: var(--fr-primary); }
.dg-timer.is-low .dg-timer__value { color: #dc2626; }
.dg-timer__track { margin-top: 0.375rem; height: 4px; width: 100%; overflow: hidden; border-radius: 999px; background: rgba(28, 28, 30, 0.1); }
.dg-timer__fill { height: 100%; border-radius: 999px; background: var(--fr-primary); transition: width 0.5s; }
.dg-timer.is-low .dg-timer__fill { background: #dc2626; }
.dg-timer__note { margin-top: 0.25rem; font-size: 10px; color: var(--fr-muted); }
.dg-redeemed { display: flex; align-items: center; gap: 0.5rem; border-radius: 0.75rem; background: #ecfdf5; padding: 0.625rem; font-size: 12px; font-weight: 600; color: #047857; box-shadow: inset 0 0 0 1px #a7f3d0; :deep(svg) { color: #059669; } span { flex: 1; } }
.dg-expired { display: flex; align-items: flex-start; gap: 0.5rem; border-radius: 0.75rem; background: var(--fr-bg); padding: 0.625rem; font-size: 11px; color: var(--fr-muted); box-shadow: inset 0 0 0 1px rgba(28, 28, 30, 0.1); :deep(svg) { color: var(--fr-primary); margin-top: 2px; flex-shrink: 0; } }
.dg-lost { display: flex; align-items: center; gap: 0.5rem; border-radius: 0.75rem; background: var(--fr-bg); padding: 0.625rem; font-size: 11px; color: var(--fr-muted); :deep(svg) { color: #059669; } }

/* ---------- Sections ---------- */
.dg-section { margin-top: 1.25rem; padding: 0 1.25rem; }
.dg-section--list { padding-bottom: 2rem; }
.dg-section__head { margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.5rem; }
.dg-section__head--tight { margin-bottom: 0.25rem; }
.dg-section__icon { color: var(--fr-primary); flex-shrink: 0; }
.dg-section__titles { min-width: 0; flex: 1; }
.dg-section__title { margin: 0; font-family: var(--fr-font-serif-display); font-size: 1rem; font-weight: 700; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dg-section__meta { font-size: 10px; color: var(--fr-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ---------- Calendar ---------- */
.dg-cal { margin: 0 -1.25rem; overflow-x: auto; padding: 0 1.25rem; scrollbar-width: none; &::-webkit-scrollbar { display: none; } }
.dg-cal__track { display: flex; gap: 0.375rem; padding-bottom: 0.25rem; }
.dg-cal__chip {
  position: relative; display: flex; height: 4rem; width: 3rem; flex-shrink: 0; flex-direction: column;
  align-items: center; justify-content: space-between; border-radius: 0.5rem; border: 1px solid rgba(28, 28, 30, 0.1);
  background: var(--fr-surface); padding: 0.375rem; cursor: pointer; transition: all 0.15s;
  &.is-wide { width: 4rem; }
  &.is-today { border-color: var(--fr-primary); background: rgba(var(--fr-primary-rgb), 0.07); box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1); }
}
.dg-cal__prefix { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-muted); }
.dg-cal__chip.is-today .dg-cal__prefix { color: var(--fr-primary); }
.dg-cal__spacer { height: 0.5rem; }
.dg-cal__num { font-family: var(--fr-font-serif-display); font-size: 1.125rem; font-weight: 900; line-height: 1; font-variant-numeric: tabular-nums; color: var(--fr-ink); }
.dg-cal__chip.is-today .dg-cal__num { color: var(--fr-primary); }
.dg-cal__status { display: flex; height: 0.875rem; align-items: center; justify-content: center; }
.dg-cal__dot { height: 4px; width: 4px; border-radius: 999px; background: rgba(107, 114, 128, 0.6); }

.dg-ico-primary { color: var(--fr-primary); }
.dg-ico-emerald { color: #059669; }
.dg-ico-muted { color: var(--fr-muted); }
.dg-ico-muted-soft { color: rgba(107, 114, 128, 0.6); }

/* ---------- List ---------- */
.dg-lockcopy { margin: 0 0 0.625rem; display: flex; align-items: flex-start; gap: 0.375rem; border-radius: 0.5rem; background: var(--fr-bg); padding: 0.375rem 0.625rem; font-size: 11px; line-height: 1.35; color: var(--fr-muted); :deep(svg) { margin-top: 2px; flex-shrink: 0; } }
.dg-locprompt { margin-bottom: 0.625rem; display: flex; align-items: center; gap: 0.5rem; border-radius: 0.75rem; padding: 0.625rem; font-size: 11px; background: rgba(var(--fr-primary-rgb), 0.07); border: 1px dashed rgba(var(--fr-primary-rgb), 0.4); }
.dg-locprompt__text { min-width: 0; flex: 1; line-height: 1.35; }
.dg-locprompt__btn { flex-shrink: 0; border: 0; border-radius: 999px; padding: 0.25rem 0.625rem; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-accent); background: var(--fr-primary); cursor: pointer; }

.dg-rows { display: flex; flex-direction: column; gap: 0.5rem; }
.dg-fav-empty {
  display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
  padding: 1.75rem 1rem 1rem; text-align: center;
  :deep(svg) { color: var(--fr-primary); }
  p { margin: 0; max-width: 16rem; font-size: 12px; line-height: 1.5; color: var(--fr-muted); }
  p :deep(svg) { vertical-align: -2px; }
}
.dg-row { display: flex; align-items: center; gap: 0.75rem; border-radius: 0.75rem; padding: 0.625rem; background: rgba(255, 255, 255, 0.7); box-shadow: inset 0 0 0 1px rgba(28, 28, 30, 0.05); transition: all 0.15s; &.is-today { background: rgba(var(--fr-primary-rgb), 0.06); box-shadow: 0 0 0 2px var(--fr-primary); } }
.dg-row__thumb-btn { border: 0; padding: 0; flex-shrink: 0; cursor: pointer; height: 3.5rem; width: 3.5rem; border-radius: 0.5rem; overflow: hidden; background: var(--fr-primary, rgb(var(--v-theme-primary))); }
.dg-row__thumb { height: 100%; width: 100%; object-fit: cover; display: block; }
.dg-row__main { min-width: 0; flex: 1; text-align: left; border: 0; background: none; padding: 0; cursor: pointer; }
.dg-row__badges { display: flex; flex-wrap: wrap; align-items: center; gap: 0.25rem 0.375rem; }
.dg-row__period { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-muted); flex-shrink: 0; }
.dg-row__today { flex-shrink: 0; border-radius: 999px; padding: 0.1875rem 0.5rem; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-accent); background: var(--fr-primary); }
.dg-row__winner { flex-shrink: 0; border-radius: 999px; background: rgba(5, 150, 105, 0.15); padding: 0.1875rem 0.5rem; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #047857; }
.dg-row__forfeit { display: inline-flex; align-items: center; gap: 2px; flex-shrink: 0; border-radius: 999px; background: rgba(28, 28, 30, 0.1); padding: 0.1875rem 0.5rem; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-muted); }
.dg-row__coupon { display: inline-flex; align-items: center; gap: 2px; flex-shrink: 0; border-radius: 999px; padding: 0.1875rem 0.5rem; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #fff; background: var(--fr-primary); }
.dg-row__media { display: inline-flex; align-items: center; gap: 3px; flex-shrink: 0; border-radius: 999px; padding: 0.125rem 0.3125rem; color: var(--fr-primary); background: rgba(var(--fr-primary-rgb), 0.12); }
.dg-row__name { margin-top: 0.1875rem; overflow-wrap: anywhere; font-family: var(--fr-font-serif-display); font-size: 0.875rem; font-weight: 700; line-height: 1.25; }
.dg-row__meta { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 10px; color: var(--fr-muted); }
.dg-row__right { display: flex; flex-shrink: 0; flex-direction: column; align-items: flex-end; gap: 0.25rem; }
.dg-row__checkin { position: relative; border: 0; border-radius: 999px; padding: 0.25rem 0.625rem; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-accent); background: var(--fr-primary); cursor: pointer; &::after { content: ''; position: absolute; inset: -10px; } }
.dg-row__status { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--fr-muted); }
.dg-row__fav { position: relative; margin-right: -0.25rem; display: grid; height: 1.5rem; width: 1.5rem; place-items: center; border: 0; background: none; border-radius: 999px; color: var(--fr-muted); cursor: pointer; &:hover { background: rgba(28, 28, 30, 0.1); } &::after { content: ''; position: absolute; inset: -10px; } }

/* ---------- Overlays (Lose / How / Ticket / Confirm) ---------- */
.dg-overlay { position: fixed; inset: 0; z-index: 2600; display: flex; align-items: flex-end; justify-content: center; background: rgba(35, 47, 62, 0.65); }
.dg-overlay--error { z-index: 2700; }
.dg-sheet { position: relative; max-height: 75dvh; display: flex; flex-direction: column; width: 100%; max-width: min(100dvw, var(--project-max-width)); background: var(--fr-surface); color: var(--fr-ink); border-radius: 1.5rem 1.5rem 0 0; animation: dg-slide-up 0.28s ease; }
.dg-sheet--ticket { text-align: center; }
.dg-sheet__body { padding: 1.5rem; display: flex; flex-direction: column; gap: 0.75rem; overflow-y: auto; min-height: 0; }
.dg-sheet__body--center { align-items: center; text-align: center; }
.dg-sheet__head { display: flex; align-items: center; justify-content: space-between; }
.dg-sheet__title { margin: 0; font-family: var(--fr-font-serif-display); font-size: 1.125rem; font-weight: 900; }
.dg-sheet__text { margin: 0; font-size: 0.8125rem; color: var(--fr-muted); line-height: 1.4; }
.dg-sheet__close { position: absolute; top: 1.25rem; right: 1.25rem; z-index: 1; border: 0; background: none; color: var(--fr-muted); cursor: pointer; padding: 0.25rem; }
.dg-ticket__badge, .dg-lose__badge { display: grid; place-items: center; height: 3rem; width: 3rem; border-radius: 999px; margin: 0 auto; }
.dg-ticket__badge { background: #ecfdf5; color: #059669; }
.dg-lose__badge { background: rgba(var(--fr-primary-rgb), 0.12); color: var(--fr-primary); }
.dg-pass-desc { display: block; overflow: auto !important; margin: 1.5rem 0 1rem 0; padding-bottom: 1rem; border-bottom: 1px solid color-mix(in srgb, var(--fr-ink, #1c1c1e) 10%, transparent); font-size: 14px; line-height: 1.5; color: var(--fr-ink); }
.dg-pass-desc :deep(.v-btn) { float: right; }
.dg-pass-desc :deep(ul) { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
.dg-pass-desc :deep(ol) { list-style: decimal; padding-left: 1.25rem; margin: 0.5rem 0; }
.dg-pass-desc :deep(li) { margin: 0.25rem 0; }
.product-info__history { padding: 0 !important; }
.dg-how { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.625rem; }
.dg-how li { display: flex; align-items: flex-start; gap: 0.625rem; font-size: 0.8125rem; line-height: 1.35; }
.dg-how__num { flex-shrink: 0; display: grid; place-items: center; height: 1.25rem; width: 1.25rem; border-radius: 999px; background: var(--fr-primary); color: var(--fr-accent); font-size: 11px; font-weight: 800; }
.dg-how__fine { margin: 0.5rem 0 0; font-size: 11px; color: var(--fr-muted); line-height: 1.4; }

@keyframes dg-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
</style>
